@if ($bulkSelectable()) {
  <span class="list-row-checkbox" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
    <wb-checkbox size="xsmall" [checked]="$selected()" (onToggle)="onSelectionToggle($event)" />
  </span>
}

<div class="list-row-left">
  @if ($nested()) {
    <svg class="list-row-nested-connector" viewBox="0 0 24 40" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      @if ($nestedLast()) {
        <path d="M7 0 V8 Q7 20 16 20 H24" />
      } @else {
        <path d="M7 0 V40" />
        <path d="M7 8 Q7 20 16 20 H24" />
      }
    </svg>
  }
  @for (entry of $leftEntries(); track entry.field.path ?? entry.field.name) {
    @if (entry.badge; as badge) {
      <w-badge class="list-row-identifier" [mode]="badge.mode" [label]="badge.label" [tooltip]="badge.tooltip" [iconLeft]="badge.iconLeft" [iconLeftColor]="badge.iconLeftColor" [color]="badge.color" [backgroundColor]="badge.backgroundColor" [borderColor]="badge.borderColor" size="xs" />
    } @else {
      <wb-value [class.list-row-title]="entry.title" [attr.title]="entry.text" [value]="entry.value" [field]="entry.field" [data]="$data()" [truncate]="entry.truncate" [compact]="true" />
    }
    @if (entry.title && $titleAdornment(); as adornment) {
      <i [class]="'list-row-title-adornment ' + adornment.icon" [class.is-error]="adornment.tone === 'error'" [title]="adornment.label" [attr.aria-label]="adornment.label"></i>
    }
    @if (entry.title && $parentProgress(); as progress) {
      <wb-value-completion class="list-row-parent-progress" [progress]="progress" />
    }
    @if (entry.title && $flatParentLabel(); as parentLabel) {
      <span class="list-row-parent" [title]="parentLabel">
        <i class="list-row-parent-divider fa-regular fa-angle-right" aria-hidden="true"></i>
        <span class="list-row-parent-name">{{ parentLabel }}</span>
      </span>
    }
  }
</div>

<div class="list-row-right">
  @for (item of $chips(); track item.id) {
    <span class="list-row-fold-item" [class.is-folded]="$folded().has(item.id)" [attr.data-fold-id]="item.id">
      <ng-container [ngTemplateOutlet]="property" [ngTemplateOutletContext]="{ $implicit: item }" />
    </span>
  }

  @if ($folded().size) {
    <span class="list-row-overflow" [pTooltip]="foldedCard" [autoHide]="false" [showDelay]="200" tooltipPosition="top" tooltipStyleClass="wb-dark-tooltip" appendTo="body">
      <w-badge [mode]="BadgeMode.Pill" [label]="'+' + $folded().size" [color]="'var(--local-list-row-property-text)'" [backgroundColor]="'var(--local-list-row-property-bg)'" [borderColor]="'var(--local-list-row-property-border)'" size="xs" />
    </span>
    <ng-template #foldedCard><wb-hover-card [title]="'More'" [rows]="$foldedRows()" /></ng-template>
  }

  @if ($pinned().length || $assigneeSlot().enabled) {
    <span class="list-row-pinned">
      @for (item of $pinned(); track item.id) {
        <ng-container [ngTemplateOutlet]="property" [ngTemplateOutletContext]="{ $implicit: item }" />
      }
      @if ($assigneeSlot().enabled) {
        <span class="list-row-slot-assignee" [pTooltip]="$assigneeSlot().tooltip" tooltipPosition="top" tooltipStyleClass="list-row-assignee-tooltip" [showDelay]="300" appendTo="body">
          @if ($avatarContent(); as content) {
            <span class="list-row-avatars" [wbContent]="content"></span>
          }
        </span>
      }
    </span>
  }

  @for (item of $metas(); track item.id) {
    <span class="list-row-fold-item list-row-meta" [class.is-folded]="$folded().has(item.id)" [attr.data-fold-id]="item.id" [pTooltip]="item.tooltip" tooltipPosition="top" [showDelay]="300" appendTo="body">{{ item.label }}</span>
  }
</div>

<ng-template #property let-item>
  @if (item.iconOnly) {
    <span class="list-row-evidence" [attr.aria-label]="item.tooltip" [attr.role]="hasPropertyAction(item) ? 'button' : null" [attr.tabindex]="hasPropertyAction(item) ? 0 : null" (click)="onPropertyClick($event, item)" (mousedown)="hasPropertyAction(item) && $event.stopPropagation()" (keydown)="onPropertyKeydown($event, item)" [pTooltip]="item.hoverCard ? hoverCard : item.tooltip" [autoHide]="false" [showDelay]="200" tooltipPosition="top" [tooltipStyleClass]="item.hoverCard ? 'wb-dark-tooltip' : ''" appendTo="body">
      <i [class]="item.iconLeft" aria-hidden="true"></i>
      @if (item.iconBadge === 'success') {
        <i class="list-row-evidence-badge is-success fa-solid fa-circle-check" aria-hidden="true"></i>
      } @else if (item.iconBadge === 'error') {
        <i class="list-row-evidence-badge is-error fa-solid fa-circle-exclamation" aria-hidden="true"></i>
      }
    </span>
  } @else if (item.dotColors) {
    <w-badge class="list-row-label-summary" [attr.role]="hasPropertyAction(item) ? 'button' : null" [attr.tabindex]="hasPropertyAction(item) ? 0 : null" (click)="onPropertyClick($event, item)" (mousedown)="hasPropertyAction(item) && $event.stopPropagation()" (keydown)="onPropertyKeydown($event, item)" [mode]="BadgeMode.Pill" [dots]="item.dotColors" [label]="item.label" [tooltip]="item.tooltip" [color]="item.color" [backgroundColor]="item.backgroundColor" [borderColor]="item.borderColor" size="xs" />
  } @else {
    <w-badge class="list-row-property" [attr.role]="hasPropertyAction(item) ? 'button' : null" [attr.tabindex]="hasPropertyAction(item) ? 0 : null" (click)="onPropertyClick($event, item)" (mousedown)="hasPropertyAction(item) && $event.stopPropagation()" (keydown)="onPropertyKeydown($event, item)" [class.is-dashed]="item.dashed" [class.is-disabled]="item.disabled" [mode]="item.mode" [label]="item.label" [tooltip]="item.hoverCard ? '' : item.tooltip" [pTooltip]="hoverCard" [tooltipDisabled]="!item.hoverCard" [autoHide]="false" [showDelay]="200" tooltipPosition="top" tooltipStyleClass="wb-dark-tooltip" appendTo="body" [iconLeft]="item.iconLeft" [iconLeftColor]="item.iconLeftColor" [color]="item.color" [backgroundColor]="item.backgroundColor" [borderColor]="item.borderColor" size="xs" />
  }
  <ng-template #hoverCard>@if (item.hoverCard; as card) {<wb-hover-card [title]="card.title" [rows]="card.rows" />}</ng-template>
</ng-template>
